import React from 'react';
import styled from 'styled-components';

export const KeyboardShortcutsContent = () => {
    return (
        <KeyboardShortcutsContainer>
            <ShortcutsSection>
                <SectionTitle>Object Actions</SectionTitle>
                <ColumnsContainer>
                    <Column>
                        <ShortcutRow>
                            <KeyboardKey>Esc</KeyboardKey>
                            <Description>Deselect current object</Description>
                        </ShortcutRow>
                        <ShortcutRow>
                            <KeyboardKey>E</KeyboardKey>
                            <Description>Edit</Description>
                        </ShortcutRow>
                        <ShortcutRow>
                            <KeyboardKey>D</KeyboardKey>
                            <Description>Duplicate</Description>
                        </ShortcutRow>
                    </Column>
                    <Column>
                        <ShortcutRow>
                            <KeyboardKey>O</KeyboardKey>
                            <Description>Toggle door/drawer open</Description>
                        </ShortcutRow>
                        <ShortcutRow>
                            <KeyboardKey>R</KeyboardKey>
                            <Description>Rotate right</Description>
                        </ShortcutRow>
                        <ShortcutRow>
                            <KeyboardKey>M</KeyboardKey>
                            <Description>Enter Movement mode</Description>
                        </ShortcutRow>
                    </Column>
                </ColumnsContainer>
                <ShortcutRow>
                    <KeyGroup>
                        <KeyboardKey>Delete</KeyboardKey>
                        <KeySeparator>or</KeySeparator>
                        <KeyboardKey>Backspace</KeyboardKey>
                    </KeyGroup>
                    <Description>Delete selected object</Description>
                </ShortcutRow>
            </ShortcutsSection>

            <ShortcutsSection>
                <SectionTitle>Object Rotation</SectionTitle>
                <ShortcutRow>
                    <KeyboardKey>Space</KeyboardKey>
                    <Description>Rotate clockwise (90°)</Description>
                </ShortcutRow>
                <ShortcutRow>
                    <KeyGroup>
                        <KeyboardKey>Shift</KeyboardKey>
                        <KeySeparator>+</KeySeparator>
                        <KeyboardKey>Space</KeyboardKey>
                    </KeyGroup>
                    <Description>Rotate counter-clockwise (90°)</Description>
                </ShortcutRow>
            </ShortcutsSection>

            <ShortcutsSection>
                <SectionTitle>Horizontal Movement</SectionTitle>
                <ShortcutRow>
                    <KeyGroup>
                        <KeyboardKey>←</KeyboardKey>
                        <KeyboardKey>→</KeyboardKey>
                        <KeyboardKey>↑</KeyboardKey>
                        <KeyboardKey>↓</KeyboardKey>
                    </KeyGroup>
                    <Description>Move 10mm</Description>
                </ShortcutRow>
                <ShortcutRow>
                    <KeyGroup>
                        <KeyboardKey>Shift</KeyboardKey>
                        <KeySeparator>+</KeySeparator>
                        <KeyboardKey>Arrow</KeyboardKey>
                    </KeyGroup>
                    <Description>Move 1mm</Description>
                </ShortcutRow>
                <ShortcutRow>
                    <KeyGroup>
                        <KeyboardKey>Ctrl/Cmd</KeyboardKey>
                        <KeySeparator>+</KeySeparator>
                        <KeyboardKey>Arrow</KeyboardKey>
                    </KeyGroup>
                    <Description>Move 100mm</Description>
                </ShortcutRow>
                <ShortcutRow>
                    <KeyGroup>
                        <KeyboardKey>Alt</KeyboardKey>
                        <KeySeparator>+</KeySeparator>
                        <KeyboardKey>Arrow</KeyboardKey>
                    </KeyGroup>
                    <Description>Move 10mm (force snapping)</Description>
                </ShortcutRow>
            </ShortcutsSection>

            <ShortcutsSection>
                <SectionTitle>Vertical Movement</SectionTitle>
                <ShortcutRow>
                    <KeyGroup>
                        <KeyboardKey>Page Up</KeyboardKey>
                        <KeyboardKey>Page Down</KeyboardKey>
                    </KeyGroup>
                    <Description>Move up/down 10mm </Description>
                </ShortcutRow>
                <ShortcutRow>
                    <KeyGroup>
                        <KeyboardKey>Shift</KeyboardKey>
                        <KeySeparator>+</KeySeparator>
                        <KeyboardKey>Page Up/Down</KeyboardKey>
                    </KeyGroup>
                    <Description>Move up/down 1mm</Description>
                </ShortcutRow>
            </ShortcutsSection>
        </KeyboardShortcutsContainer>
    );
};

const KeyboardShortcutsContainer = styled.div`
    padding: 16px 20px;
    font-family: ${({theme}) => theme.typography.fontStyle.fontFamily};
`;

const ShortcutsSection = styled.div`
    margin-bottom: 16px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const SectionTitle = styled.h3`
    font-size: ${({theme}) => theme.typography.fontSize.h3};
    font-weight: 600;
    margin-bottom: 8px;
    color: ${({theme}) => theme.colors.primary.main};
    border-bottom: 2px solid #e0e0e0;
    padding-bottom: 4px;
`;

const ColumnsContainer = styled.div`
    display: flex;
    gap: 32px; /* spacing between the two columns */
    @media (max-width: 600px) {
        flex-direction: column; /* Stack vertically on small screens if needed */
        gap: 0;
    }
`;

const Column = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const ShortcutRow = styled.div`
    display: flex;
    align-items: center;
    padding: 4px 0;
    gap: 16px;
`;

const KeyGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 200px;
    flex-shrink: 0;
`;

const KeySeparator = styled.span`
    color: ${({theme}) => theme.colors.text.main};
    opacity: 0.5;
    font-size: ${({theme}) => theme.typography.fontSize.h4};
`;

const KeyboardKey = styled.kbd`
    display: inline-block;
    padding: 4px 10px;
    font-family: 'Courier New', monospace;
    font-size: ${({theme}) => theme.typography.fontSize.h4};
    font-weight: 600;
    color: ${({theme}) => theme.colors.text.main};
    background: linear-gradient(180deg, #ffffff 0%, #f5f5f5 100%);
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow:
        0 2px 0 rgba(0, 0, 0, 0.1),
        0 0 0 1px #fff inset;
    white-space: nowrap;
    min-width: 32px;
    text-align: center;
`;

const Description = styled.span`
    color: ${({theme}) => theme.colors.text.main};
    opacity: 0.8;
    font-size: ${({theme}) => theme.typography.fontSize.p};
    line-height: 1.5;
`;
